import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, ShieldCheck, Lock, FileText, Phone, Server, AlertTriangle, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PolicySection {
  icon: React.ReactNode;
  title: string;
  content: string[];
}

const defaultSections: PolicySection[] = [
  {
    icon: <ShieldCheck className="h-5 w-5 text-primary" />,
    title: "الموافقة المستنيرة",
    content: [
      "يتم الحصول على موافقة المراجع الإلكترونية قبل بدء أي جلسة طب اتصالي.",
      "يحق للمراجع الانسحاب من الخدمة في أي وقت دون أي التزامات.",
      "يتم إبلاغ المراجع بطبيعة الخدمة ومحدودياتها قبل الحجز."
    ]
  },
  {
    icon: <Lock className="h-5 w-5 text-primary" />,
    title: "حماية البيانات الشخصية (PDPL)",
    content: [
      "نلتزم بنظام حماية البيانات الشخصية في المملكة العربية السعودية.",
      "يتم جمع ومعالجة البيانات الصحية لغرض تقديم الخدمة فقط.",
      "لا يتم مشاركة البيانات مع أطراف ثالثة دون موافقة صريحة.",
      "يحق للمراجع طلب الاطلاع على بياناته أو حذفها في أي وقت."
    ]
  },
  {
    icon: <Server className="h-5 w-5 text-primary" />,
    title: "أمن المعلومات والتشفير",
    content: [
      "جميع الاتصالات محمية بتشفير من طرف إلى طرف (End-to-End Encryption).",
      "نلتزم بضوابط الهيئة الوطنية للأمن السيبراني (NCA).",
      "يتم استخدام بروتوكولات أمان متوافقة مع معايير HIPAA العالمية.",
      "يتم تسجيل جميع عمليات الوصول والتعديل على البيانات لأغراض المراجعة."
    ]
  },
  {
    icon: <FileText className="h-5 w-5 text-primary" />,
    title: "التحقق من الهوية",
    content: [
      "يُطلب من المراجع تقديم رقم الهوية الوطنية أو الإقامة عند التسجيل.",
      "يتم التحقق من هوية الممارس الصحي عبر الترخيص المهني المعتمد.",
      "جميع الممارسين مسجلون لدى الهيئة السعودية للتخصصات الصحية (SCFHS)."
    ]
  },
  {
    icon: <AlertTriangle className="h-5 w-5 text-primary" />,
    title: "بروتوكول الطوارئ الطبية",
    content: [
      "في حالة الطوارئ الطبية، يجب الاتصال برقم الطوارئ الموحد 911 فوراً.",
      "لا تُغني خدمات الطب الاتصالي عن الزيارة الشخصية في الحالات الحرجة.",
      "يتوفر بروتوكول واضح للتعامل مع الانقطاع التقني أثناء الجلسات.",
      "في حال انقطاع الاتصال، يتم التواصل عبر المحادثة النصية أو إعادة الجدولة."
    ]
  },
  {
    icon: <Phone className="h-5 w-5 text-primary" />,
    title: "حقوق المراجع والشكاوى",
    content: [
      "يحق للمراجع تقديم شكوى عبر نظام الشكاوى المدمج في المنصة.",
      "يتم التعامل مع جميع الشكاوى خلال 5 أيام عمل كحد أقصى.",
      "يحق للمراجع الحصول على نسخة من سجله الصحي الإلكتروني.",
      "يمكن التواصل مع إدارة المنصة لأي استفسارات أو ملاحظات."
    ]
  }
];

const ClinicTelehealthPolicy = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [policyContent, setPolicyContent] = useState("");
  const [editContent, setEditContent] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [hasCustomPolicy, setHasCustomPolicy] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    loadPolicy();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: hasAdminRole } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });
      setIsAdmin(!!hasAdminRole);
    }
  };

  const loadPolicy = async () => {
    const { data } = await supabase
      .from('policies')
      .select('content_ar')
      .eq('policy_key', 'telehealth_policy')
      .single();

    if (data?.content_ar) {
      setPolicyContent(data.content_ar);
      setHasCustomPolicy(true);
    }
  };

  const handleEditClick = () => {
    if (hasCustomPolicy) {
      setEditContent(policyContent);
    } else {
      // Convert default sections to editable text
      const text = defaultSections.map(s => `## ${s.title}\n${s.content.join('\n')}`).join('\n\n');
      setEditContent(text);
    }
    setIsEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (hasCustomPolicy) {
      const { error } = await supabase
        .from('policies')
        .update({ content_ar: editContent })
        .eq('policy_key', 'telehealth_policy');
      if (error) {
        toast.error("حدث خطأ أثناء حفظ التعديلات");
        return;
      }
    } else {
      const { error } = await supabase
        .from('policies')
        .insert({ policy_key: 'telehealth_policy', title_ar: 'سياسة الطب الاتصالي', content_ar: editContent });
      if (error) {
        toast.error("حدث خطأ أثناء حفظ التعديلات");
        return;
      }
      setHasCustomPolicy(true);
    }
    setPolicyContent(editContent);
    setIsEditDialogOpen(false);
    toast.success("تم حفظ التعديلات بنجاح");
  };

  // Parse custom policy content into sections
  const parseCustomContent = (content: string): PolicySection[] => {
    const icons = [
      <ShieldCheck className="h-5 w-5 text-primary" />,
      <Lock className="h-5 w-5 text-primary" />,
      <Server className="h-5 w-5 text-primary" />,
      <FileText className="h-5 w-5 text-primary" />,
      <AlertTriangle className="h-5 w-5 text-primary" />,
      <Phone className="h-5 w-5 text-primary" />,
    ];
    
    const sectionTexts = content.split(/^## /m).filter(Boolean);
    return sectionTexts.map((section, i) => {
      const lines = section.trim().split('\n');
      const title = lines[0] || '';
      const contentLines = lines.slice(1).filter(l => l.trim());
      return {
        icon: icons[i % icons.length],
        title,
        content: contentLines,
      };
    });
  };

  const sections = hasCustomPolicy ? parseCustomContent(policyContent) : defaultSections;

  return (
    <div className="min-h-screen py-8 px-4" dir="rtl">
      <div className="container mx-auto max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {slug && (
              <Button variant="ghost" size="icon" onClick={() => navigate(`/clinic/${slug}`)}>
                <ArrowRight className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold">سياسة الخصوصية والطب الاتصالي</h1>
              <p className="text-sm text-muted-foreground">
                وفقاً لاشتراطات وزارة الصحة ومجلس الصحة السعودي
              </p>
            </div>
          </div>

          {isAdmin && (
            <Button variant="outline" onClick={handleEditClick} className="gap-2">
              <Edit className="h-4 w-4" />
              تعديل
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {sections.map((section, index) => (
            <Card key={index}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  {section.icon}
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          <p>آخر تحديث: مارس 2026</p>
          <p className="mt-1">هذه السياسة معدّة وفقاً لأنظمة المملكة العربية السعودية المعمول بها</p>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]" dir="rtl">
          <DialogHeader>
            <DialogTitle>تعديل سياسة الطب الاتصالي</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">استخدم ## لعنوان كل قسم، وكل سطر جديد يمثل بنداً</p>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[400px] font-mono text-sm"
              dir="rtl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleSave}>حفظ التعديلات</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClinicTelehealthPolicy;
